import React, { useMemo } from 'react'
import { Chip, Group, iconSizes, Paper, Select, Text } from '@common/ui'
import { com } from '@eidu/web'
import { IconChevronLeft, IconChevronRight } from '@common/ui/icons'
import { EntityPathNode } from './EntityPathUtils'
import EntityPath = com.eidu.entity.path.EntityPath

export type RenderablePath = {
  entityPath: EntityPath
  entityPathLabel: string
  entityPathNodes: readonly EntityPathNode[]
}

const PathRenderComponent: React.FC<{ path: RenderablePath }> = ({ path }) => (
  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 6 }}>
    {path.entityPathNodes.map((node: EntityPathNode) => (
      <React.Fragment key={node.id}>
        {node.leftField && <IconChevronLeft size={iconSizes.sm} stroke={1.5} />}
        <Paper withBorder px="sm" py="xxs" radius="sm">
          <Group gap="xs" wrap="nowrap">
            {node.leftField && <Chip size="sm">{node.leftField}</Chip>}
            <span>{node.type}</span>
            {node.rightField && <Chip size="sm">{node.rightField}</Chip>}
          </Group>
        </Paper>
        {node.rightField && <IconChevronRight size={iconSizes.sm} stroke={1.5} />}
      </React.Fragment>
    ))}
  </div>
)

type PathFieldProps = {
  paths: readonly RenderablePath[]
  selectedPath?: EntityPath
  onChange: (path?: EntityPath) => void
  disabled?: boolean
}

const PathField: React.FC<PathFieldProps> = ({ paths, selectedPath, onChange, disabled = false }) => {
  const hasError = useMemo(
    () =>
      !(selectedPath instanceof EntityPath) || paths.find((path) => path.entityPath.equals(selectedPath)) === undefined,
    [paths, selectedPath]
  )
  const selectedIndex = paths.findIndex((path) => path.entityPath.equals(selectedPath))
  if (paths.length === 0) {
    return (
      <Text size="sm" c="negative">
        No paths found
      </Text>
    )
  } else {
    return (
      <Select
        label="Assignment"
        data={paths.map((path, index) => ({ value: String(index), label: path.entityPathLabel }))}
        renderOption={({ option }) => <PathRenderComponent path={paths[Number(option.value)]} />}
        value={selectedIndex >= 0 ? String(selectedIndex) : null}
        onChange={(value) => {
          if (value !== null) onChange(paths[Number(value)].entityPath)
        }}
        disabled={disabled}
        error={hasError ? 'Warning: Please select a path.' : undefined}
        allowDeselect={false}
      />
    )
  }
}

export default PathField
